import { HUD } from '../../config/tuning';
import type { HudPainter } from '../HudLayout';
import { TextSlot } from '../HudLayout';
import type { HudFrame } from '../HudFrame';

/**
 * The bottom-right cluster (SPEC §3.4.1): link quality and countermeasure counts.
 *
 * Link quality is not decoration. It falls with range from the control station, with terrain
 * occlusion, and inside jamming — and the picture degrades with it (SPEC §3.3.2 item 6). The
 * bar exists so a player who has turned the compression artefacts off for accessibility or
 * performance still gets the warning that they are being jammed.
 */

/** Number of lit bars for a link quality in [0, 1]. */
export function litLinkBars(linkQuality: number): number {
  if (linkQuality <= 0) return 0;
  return Math.max(1, Math.min(HUD.linkBars, Math.ceil(linkQuality * HUD.linkBars)));
}

export function drawStatus(painter: HudPainter, frame: HudFrame): void {
  const layout = painter.layout;
  const ctx = painter.ctx;
  const margin = layout.px(HUD.marginPx);
  const gap = layout.px(HUD.readoutGapPx);

  const rightEdge = layout.widthPx - margin;
  const bottom = layout.heightPx - margin;

  // Countermeasures.
  painter.text(
    `CH ${painter.number(TextSlot.Chaff, frame.chaffRemaining, 0)}`,
    rightEdge,
    bottom,
    'right',
  );
  painter.text(
    `FL ${painter.number(TextSlot.Flares, frame.flaresRemaining, 0)}`,
    rightEdge,
    bottom - gap,
    'right',
  );

  // Four-bar link indicator, rising to the right.
  const barWidth = layout.px(HUD.linkBarWidthPx);
  const step = layout.px(HUD.linkBarStepPx);
  const base = layout.px(HUD.linkBarBasePx);
  const lit = litLinkBars(frame.linkQuality);
  const barsWidth = HUD.linkBars * (barWidth + step * 0.5);
  const barsY = bottom - gap * 2.4;
  const startX = rightEdge - barsWidth;

  ctx.save();
  for (let i = 0; i < HUD.linkBars; i += 1) {
    const height = base + step * i;
    const x = startX + i * (barWidth + step * 0.5);
    ctx.beginPath();
    ctx.rect(x, barsY - height, barWidth, height);
    if (i < lit) {
      ctx.fillStyle = painter.currentTheme.colour;
      ctx.fill();
    }
    painter.stroke(0.8);
  }
  ctx.restore();

  painter.text('LINK', startX - gap * 0.4, barsY - base * 0.5, 'right');
}
